import { useCallback } from 'react'

import { useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'

import { useNotifications } from '@sequencehq/core-components'
import { ServerAsset } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

import { isContractsUploadingAtom } from './atoms'
import { automationsPaths } from 'Automations/router'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { useCurrentUser } from 'common/hooks'

export const useContractUpload = () => {
  const navigate = useNavigate()
  const { displayNotification, hideNotification } = useNotifications()
  const setIsUploading = useSetAtom(isContractsUploadingAtom)
  const currentUser = useCurrentUser()
  const { logEvent } = useLogEvent()

  const startAutomation = useCallback(
    async (assetId: string, fileName: string) => {
      const res = await dashboardv99990101Client.postStartAutomation({
        triggerType: 'CONTRACT_UPLOADED',
        input: {
          assetId,
          requestorEmail: currentUser?.email || '',
          fileName
        }
      })
      if (res.error || !res.data) {
        return null
      }
      return { automationExecutionId: res.data.id }
    },
    [currentUser]
  )

  const onFilesPicked = useCallback(
    (files: File[]) => {
      setIsUploading(true)

      files.forEach(file => {
        displayNotification(`Uploading ${file.name}`, {
          type: 'loading',
          id: file.name,
          duration: Infinity
        })
      })
    },
    [displayNotification, setIsUploading]
  )

  const onUploadSuccess = useCallback(
    async (asset: ServerAsset) => {
      logEvent({
        eventName: 'watchtower_contract_uploaded',
        metadata: {
          assetId: asset.id
        }
      })
      const automationRes = await startAutomation(asset.id, asset.fileName)

      hideNotification(asset.fileName)

      if (!automationRes) {
        displayNotification(`Error creating workflow for ${asset.fileName}`, {
          type: 'error'
        })
        return
      }

      displayNotification(`Uploaded ${asset.fileName}`, {
        type: 'success',
        'data-testid': 'contractUpload.successToast',
        viewLabel: 'View workflow',
        onClickView: () =>
          navigate(
            automationsPaths.executionHistoryPreview(
              automationRes.automationExecutionId
            )
          )
      })
    },
    [startAutomation, hideNotification, displayNotification, navigate, logEvent]
  )

  const onUploadFailure = useCallback(
    (error: string) => {
      displayNotification(error, { type: 'error' })
    },
    [displayNotification]
  )

  const onAllUploadsComplete = useCallback(() => {
    setIsUploading(false)
  }, [setIsUploading])

  return {
    onFilesPicked,
    onUploadSuccess,
    onUploadFailure,
    onAllUploadsComplete
  }
}
